import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, KeyRound, Trash2, Loader2, UserCog } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CARD, Label, Loader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label as UiLabel } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LoginJournal } from "@/components/LoginJournal";

const ROLES = [
  { v: "owner", l: "Propriétaire" },
  { v: "manager", l: "Directeur / gestionnaire" },
  { v: "secretary", l: "Secrétaire" },
  { v: "practitioner", l: "Praticien" },
  { v: "hr", l: "Ressources humaines" },
  { v: "hygiene", l: "Référent hygiène" },
  { v: "viewer", l: "Lecture seule" },
];
const strongPw = (p) => p.length >= 10 && /[A-Za-z]/.test(p) && /\d/.test(p);
const roleLabel = (v) => (ROLES.find((r) => r.v === v)?.l) || v;

export default function Profils() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [prats, setPrats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [pw, setPw] = useState("");
  const [del, setDel] = useState(null);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "secretary", practitioner_id: "" });
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("users");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, p] = await Promise.all([api.get("/team/users"), api.get("/practitioners")]);
      setUsers(u.data); setPrats(p.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!f.email || !strongPw(f.password)) { toast.error("Email requis et mot de passe d'au moins 10 caractères avec lettre et chiffre"); return; }
    setSaving(true);
    try {
      const payload = { ...f, practitioner_id: f.practitioner_id === "none" ? null : f.practitioner_id || null };
      await api.post("/team/users", payload);
      toast.success("Profil créé");
      setDialog(false);
      setF({ name: "", email: "", password: "", role: "secretary", practitioner_id: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const setRole = async (u, role) => { try { await api.put(`/team/users/${u.id}`, { role }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const setPrat = async (u, pid) => { try { await api.put(`/team/users/${u.id}`, { practitioner_id: pid === "none" ? null : pid }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const toggleActive = async (u) => { try { await api.put(`/team/users/${u.id}`, { active: u.active === false }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const resetPw = async () => {
    if (!strongPw(pw)) { toast.error("Minimum 10 caractères, avec au moins une lettre et un chiffre"); return; }
    try { await api.post(`/team/users/${pwUser.id}/password`, { password: pw }); toast.success("Mot de passe réinitialisé"); setPwUser(null); setPw(""); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doDelete = async () => { try { await api.delete(`/team/users/${del.id}`); toast.success("Profil supprimé"); setDel(null); load(); } catch (e) { toast.error(apiErr(e)); } };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <UserCog className="h-6 w-6 text-[#065F46]" />
          <div>
            <p className="text-sm text-gray-400">Gestion des accès</p>
            <h1 className="font-display text-2xl font-bold sm:text-3xl">Profils</h1>
          </div>
        </div>
        {view === "users" && <Button data-testid="add-user-btn" onClick={() => setDialog(true)} className="rounded-full bg-[#065F46] hover:bg-[#047857]"><Plus className="mr-1.5 h-4 w-4" />Nouveau profil</Button>}
      </div>

      <div className="mt-4 flex gap-2">
        {[["users", "Utilisateurs"], ["journal", "Journal des connexions"]].map(([k, l]) => (
          <button type="button" key={k} data-testid={`profils-tab-${k}`} onClick={() => setView(k)} className={`rounded-full px-4 py-2 text-sm font-semibold ${view === k ? "bg-[#065F46] text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}>{l}</button>
        ))}
      </div>

      {view === "journal" && <LoginJournal users={users} />}
      {view === "users" && <div className={`${CARD} mt-5 p-4 sm:p-6`}>
        {users.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-3 border-b border-gray-50 py-3 last:border-0" data-testid={`user-${u.id}`}>
            <span className={`flex h-10 w-10 items-center justify-center rounded-full font-bold ${u.active === false ? "bg-gray-100 text-gray-400" : "bg-emerald-50 text-[#065F46]"}`}>
              {(u.name || u.email).charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{u.name || u.email} {u.id === me?.id && <span className="text-xs text-gray-400">(vous)</span>}</p>
              <p className="truncate text-xs text-gray-400">{u.email} · {roleLabel(u.role)}{u.practitioner_name ? ` · ${u.practitioner_name}` : ""}</p>
            </div>
            <Select value={u.role} onValueChange={(v) => setRole(u, v)}>
              <SelectTrigger className="h-9 w-36 rounded-full text-xs" data-testid={`role-${u.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
            </Select>
            {["practitioner", "owner", "admin", "manager"].includes(u.role) && (
              <Select value={u.practitioner_id || "none"} onValueChange={(v) => setPrat(u, v)}>
                <SelectTrigger className="h-9 w-36 rounded-full text-xs" data-testid={`link-${u.id}`}><SelectValue placeholder="Lier praticien" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{u.role === "practitioner" ? "Non lié" : "Pas d'espace praticien"}</SelectItem>
                  {prats.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Switch checked={u.active !== false} onCheckedChange={() => toggleActive(u)} data-testid={`active-${u.id}`} />
            <button onClick={() => { setPwUser(u); setPw(""); }} data-testid={`resetpw-${u.id}`} className="rounded-lg p-2 text-gray-400 hover:bg-amber-50 hover:text-amber-600" title="Réinitialiser le mot de passe"><KeyRound className="h-4 w-4" /></button>
            <button disabled={u.id === me?.id} onClick={() => setDel(u)} data-testid={`delete-user-${u.id}`} className="rounded-lg p-2 text-gray-400 enabled:hover:bg-rose-50 enabled:hover:text-rose-600 disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="rounded-3xl sm:max-w-md" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Nouveau profil</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><UiLabel>Nom</UiLabel><Input data-testid="u-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5 rounded-xl" /></div>
            <div><UiLabel>Email</UiLabel><Input data-testid="u-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5 rounded-xl" /></div>
            <div><UiLabel>Mot de passe (min 10, lettre + chiffre)</UiLabel><Input data-testid="u-password" type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="mt-1.5 rounded-xl" /></div>
            <div>
              <UiLabel>Rôle</UiLabel>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger data-testid="u-role" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {f.role === "practitioner" && (
              <div>
                <UiLabel>Praticien associé</UiLabel>
                <Select value={f.practitioner_id || "none"} onValueChange={(v) => setF({ ...f, practitioner_id: v })}>
                  <SelectTrigger data-testid="u-link" className="mt-1.5 rounded-xl"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non lié</SelectItem>
                    {prats.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button data-testid="u-submit" onClick={create} disabled={saving} className="w-full rounded-full bg-[#065F46] py-6 hover:bg-[#047857]">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Créer le profil"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwUser} onOpenChange={(v) => !v && setPwUser(null)}>
        <DialogContent className="rounded-3xl sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Réinitialiser le mot de passe</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-gray-500">Pour <b>{pwUser?.email}</b>.</p>
            <Input data-testid="pw-input" type="text" value={pw} onChange={(e) => setPw(e.target.value)} className="rounded-xl" placeholder="Nouveau mot de passe (min 10, lettre + chiffre)" />
            <Button data-testid="pw-submit" onClick={resetPw} className="w-full rounded-full bg-[#065F46] py-6 hover:bg-[#047857]">Réinitialiser</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader><AlertDialogTitle>Supprimer {del?.email} ?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-user" className="rounded-full bg-rose-600 hover:bg-rose-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
